<script setup>
import IllustrationSplash from './IllustrationSplash.vue'
import { withTrailingSlash } from '@/utils/siteUrl'

const props = defineProps({
  display: { type: String, default: 'graphic' },  // graphic (colour block + logo) | image (real project photo) | splash (decorative illustration)
  logo:    { type: Object, default: null },   // { src, alt } — used when display === 'graphic'
  image:   { type: Object, default: null },   // { src, alt } — used when display === 'image'
  // Used when display === 'splash' — an IllustrationSplash (smallCardTonal
  // family) variation letter (A-F), or '' for the default ('E' — safe on
  // both top AND bottom per SMALL_CARD_SAFE_EDGES, the only letter that is;
  // .content sits directly below .media here, not beside it, so the safe
  // edge that matters is the *bottom* one, not left/right like ContentHero's
  // side-by-side layout needs. Not 'A', which has a known unfixed "weird
  // bump" — see smallCardIllustrations.js). Fixed, not random — a single
  // always-rendered card has the same SSR/hydration-flash risk ContentHero's
  // random pick had (jk-21 Task 9), and there's no per-page variety need to
  // justify solving that here.
  splash:  { type: String, default: '' },
  colour:  { type: String, default: 'pink' },  // pink | yellow | lime | cream
  title:   { type: String, default: '' },     // optional short "snappy" project title (home cards only); omitted when blank
  body:    { type: String, required: true },
  cta:     { type: Object, required: true },    // { label, to }
})
</script>

<template>
  <RouterLink :to="withTrailingSlash(cta.to)" class="impact-card" :class="colour">
    <div v-if="display === 'splash'" class="media">
      <!-- colour passes straight through to ILLUSTRATION_COLOURS (jk-21) —
           only 'pink'/'lime' are real keys there today, matching this card's
           own 'pink'/'lime' background exactly (same --color-card-* var).
           'yellow'/'cream' aren't in that registry yet; IllustrationSplash
           silently falls back to its own 'pink' set if either is ever used
           here — fine today (no card does), but flag it if that changes.
           contentSide="bottom" keeps the line art clear of .content below —
           harmless even for 'E' (already safe top+bottom, no flip fires),
           and self-corrects if `splash` is ever overridden to a variation
           that actually needs the flip to reach a safe bottom edge. -->
      <IllustrationSplash family="smallCardTonal" :colour="colour" :variation="splash || 'E'" contentSide="bottom" class="splash-illo" />
    </div>
    <div v-else-if="display === 'image' && image" class="media">
      <img :src="image.src" :alt="image.alt" loading="lazy" />
    </div>
    <div v-else-if="logo" class="logo">
      <img :src="logo.src" :alt="logo.alt" />
    </div>
    <div class="content" :class="{ 'has-title': title }">
      <h3 v-if="title" class="title">{{ title }}</h3>
      <p class="body">{{ body }}</p>
      <span class="link">{{ cta.label }} →</span>
    </div>
  </RouterLink>
</template>

<style lang="scss" scoped>
.impact-card {
  // Card-level padding removed — .media now bleeds to the card's own top/
  // left/right edges instead of floating inset inside a frame. Padding
  // lives only on .content (and a lighter one on .logo, which is a small
  // mark, not a bleed photo).
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  background: var(--card-bg);
  @include rounded($radius);
  @include transition(box-shadow, background, transform);

  // Interactivity now matches the other card families (jk-34): slight lift
  // on hover, background lightens a touch (color-mix against white — no
  // per-colour "-light" token exists for pink/yellow/lime, unlike blue/green),
  // and the CTA's underline reveal (below) draws in.
  &:hover {
    background: color-mix(in srgb, var(--card-bg) 90%, white 10%);
    transform: translateY(-2px);
    // `.content .link`, not just `.link` (jk-34 fix) — the base rule below
    // is scoped to `.impact-card .content .link` (equal specificity to a
    // plain `.impact-card:hover .link`), and it comes later in this file,
    // so on equal specificity it was winning the cascade and the reveal
    // never actually fired. Matching its exact scope here always outranks
    // it regardless of source order.
    .content .link { background-size: 100% 1px; }
  }
  &:focus-visible {
    @include focus-ring;
    outline: none;
    .content .link { background-size: 100% 1px; }
  }
  &:active { transform: translateY(1px); }

  &.pink   { --card-bg: var(--color-card-pink); }
  &.yellow { --card-bg: var(--color-card-yellow); }
  &.lime   { --card-bg: var(--color-card-lime); }
  &.cream  { --card-bg: var(--color-surface-warm); }

  // Cream matches Training/Resource cards' own CTA styling (bold primary
  // colour) rather than the other colours' ink-coloured link.
  &.cream .link {
    color: var(--color-primary);
    font-weight: 600;
  }

  // Starting value — was 520px, which made the "Our impact" band very tall.
  // Grid stretch already equalises the three cards' heights; this just sets a
  // sensible floor. Tune on visual sign-off.
  @include from-md { min-height: 360px; }

  .logo {
    padding: $space-6 $space-6 0;

    img {
      display: block;
      height: 80px;
      width: auto;
      max-width: 100%;
      object-fit: contain;
    }
  }

  // Real project photo, replacing the logo block — same aspect ratio as the
  // /our-work listing cards (design-system hygiene: one photo treatment,
  // reused). Carries the card's own radius on all 4 corners: the top two
  // coincide exactly with the card's own rounding (invisible seam), the
  // bottom two (where it meets .content) round visibly against the card's
  // colour.
  .media {
    aspect-ratio: 16 / 9;
    width: 100%;
    @include rounded($radius);
    overflow: hidden;

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: top;
    }

    // Overrides SmallCardTonalSplash's own default aspect-ratio (434:272) so
    // it fills .media's actual box (16:9) exactly, same technique as
    // ContentHero.vue's illustration override.
    .splash-illo {
      width: 100%;
      height: 100%;
      aspect-ratio: auto;
    }
  }

  .content {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: $space-6;
    padding: $space-6;

    .title {
      margin: 0;
      font-size: $font-size-h4;
      line-height: 1.25;
      letter-spacing: $ls-heading;
      font-weight: 600;
      color: var(--color-ink);
    }

    .body {
      margin: 0;
      // Shrunk from $font-size-h3 — cards now often carry a full our-work
      // excerpt (1 sentence, ~150-250 characters) rather than a short teaser
      // blurb, and the heading size overflowed the card at that length.
      font-size: $font-size-h4;
      line-height: 1.3;
      letter-spacing: $ls-heading;
      color: var(--color-ink);
    }

    // When a snappy title is present it takes the heading role, so the excerpt
    // drops to body-text size and title+excerpt group together above a
    // bottom-anchored link (instead of the default two-item space-between).
    &.has-title {
      justify-content: flex-start;
      gap: $space-3;

      .body {
        font-size: $font-size-base;
        line-height: 1.5;
        letter-spacing: normal;
      }

      .link { margin-top: auto; }
    }

    // Unederlined at rest; the underline draws in left-to-right on hover/
    // focus (see `.impact-card`'s :hover/:focus-visible above), matching the
    // shared `.cta` treatment (_components.scss) used by the other card
    // families — kept local rather than switching to the shared `.cta` class
    // itself, since its default colour (primary) doesn't suit the solid
    // pink/yellow/lime fills this card also renders on.
    .link {
      align-self: flex-start;
      font-weight: 500;
      color: var(--color-ink);
      text-decoration: none;
      background-image: linear-gradient(currentColor, currentColor);
      background-repeat: no-repeat;
      background-position: 0 100%;
      background-size: 0% 1px;
      padding-bottom: 2px;
      @include transition(background-size);
    }
  }
}
</style>
